import { useState } from 'react'
import styled from '@emotion/styled'

import { isPlaceholder } from 'src/lib/matches/map-image'
import { Badge } from 'src/screens/matches/collection/match/Badge'
import { formattedDate } from 'src/lib/matches/matches'
import {
  Status,
  useComputedStatus
} from 'src/screens/matches/collection/match/status/Status'
import { DefuseBar } from 'src/screens/matches/collection/match/status/DefuseBar'
import { PlatformIcon } from 'src/system/ui/match/PlatformIcon'
import { ViewMatchButton } from 'src/screens/matches/collection/list/ViewMatchButton'
import type { MatchProps } from 'src/screens/matches/collection/MatchCard'
import { MatchLink } from 'src/screens/matches/collection/match/MatchLink'
import { ErrorCodeModal } from 'src/screens/matches/collection/match/status/ErrorCodeModal'
import { ProcessingModal } from 'src/screens/matches/collection/match/status/ProcessingModal'
import { StatusDescription } from 'src/screens/matches/collection/match/status/StatusDescription'
import { hoverAmount, hoverTransition } from 'src/system/ui/theme/hover'
import { Score } from 'src/system/ui/match/Score'
import { PendingMatchStatus } from 'src/graphql/types'

export function CardMobile(
  props: MatchProps & {
    background: string
  }
) {
  const [modalOpen, setModalOpen] = useState(false)
  const status = useComputedStatus(
    props.updatedAt,
    props.status,
    props.courseProgress
  )
  const canViewMatch = status === PendingMatchStatus.Completed
  const isFailed = status === PendingMatchStatus.Failed && !!props.errorCode
  const isProcessing =
    status === PendingMatchStatus.Created ||
    status === PendingMatchStatus.Verified
  const isRetired = status === PendingMatchStatus.Retired
  return (
    <>
      <StyledMatchLink
        background={props.background}
        isPlaceholderBackground={isPlaceholder(props.background)}
        link={canViewMatch ? props.link : null}
        onClick={
          isFailed || isProcessing ? () => setModalOpen(true) : undefined
        }
      >
        <Left>
          <BadgeContainer>
            <StyledBadge map={props.map} />
          </BadgeContainer>
          <Details>
            {props.showScore && (
              <StyledScore
                ctScore={props.ctScore}
                tScore={props.tScore}
                players={props.players}
                didWin={props.didWin}
                isTie={props.isTie}
                isOwnerInMatch={props.isOwnerInMatch}
              />
            )}
            <Date>
              <PlatformIcon size={15} platform={props.platform} />{' '}
              {formattedDate(props.uploadDate)}
            </Date>
          </Details>
        </Left>
        <Right>
          <StyledStatus
            current={props.status}
            updatedAt={props.updatedAt}
            createdAt={props.createdAt}
            courseProgress={props.courseProgress}
          />
          {isRetired ? (
            <RetiredDescription
              status={props.status}
              updatedAt={props.updatedAt}
              courseProgress={props.courseProgress}
            />
          ) : (
            <StyledViewMatchButton disabled={!canViewMatch} />
          )}
        </Right>
        {isProcessing && (
          <DefuseBar createdAt={props.createdAt} status={status} />
        )}
      </StyledMatchLink>
      {isFailed && props.errorCode && (
        <ErrorCodeModal
          errorCode={props.errorCode}
          matchId={props.sharecode}
          isVisible={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
      {isProcessing && (
        <ProcessingModal
          status={status}
          createdAt={props.createdAt}
          isVisible={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  )
}

const StyledMatchLink = styled(MatchLink)<{
  background: string
  isPlaceholderBackground: boolean
}>`
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  height: 100px;
  transition: filter ${hoverTransition};
  padding: ${(props) =>
    `${props.theme.d.spacing[5]} ${props.theme.d.spacing[2]}`};
  background: ${(props) => props.theme.d.colors.mapCardBackground},
    url(${(props) => props.background});
  background-size: ${(props) =>
    props.isPlaceholderBackground ? 'initial' : 'cover'};
  background-position: 50%;
  background-repeat: no-repeat;
  margin-bottom: ${(props) => props.theme.d.spacing[3]};
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  border: 1px solid ${(props) => props.theme.d.colors.border.dark};

  &:hover {
    filter: brightness(${1 + hoverAmount.fill});
  }

  display: flex;
  @media (min-width: ${(props) => props.theme.d.breakpoints.md}) {
    display: none;
  }
`

const Left = styled.div`
  display: flex;
  align-items: center;
`

const badgeSize = '60px'

const BadgeContainer = styled.div`
  width: ${badgeSize};
  height: ${badgeSize};
  display: flex;
  align-items: center;
  margin-right: ${(props) => props.theme.d.spacing[3]};
  justify-content: center;
`

const StyledBadge = styled(Badge)`
  max-width: ${badgeSize};
  max-height: ${badgeSize};
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const StyledScore = styled(Score)`
  font-size: 30px;
`

const Date = styled.span`
  color: ${(props) => props.theme.d.colors.text.secondary};
`

const StyledStatus = styled(Status)`
  font-size: 11px;
  justify-content: flex-end;
  margin-top: ${(props) => props.theme.d.spacing[1]};
`

const Right = styled.div`
  display: flex;
  flex-direction: column;
`
// A retired match can't be opened, so its card says why in place of the
// disabled button.
const RetiredDescription = styled(StatusDescription)`
  font-size: 11px;
  text-align: right;
  max-width: 140px;
  margin: ${(props) =>
    `${props.theme.d.spacing[2]} 0 ${props.theme.d.spacing[1]}`};
`

const StyledViewMatchButton = styled(ViewMatchButton)`
  margin: ${(props) =>
    `${props.theme.d.spacing[2]} 0 ${props.theme.d.spacing[1]}`};
  font-size: 14px;

  svg {
    font-size: 16px;
  }
`
